import { ArrowLeft, Eye, EyeClosed } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useIsDesktopViewport } from '../../../hooks/useIsDesktopViewport';
import { emailSetPassword, getAuthError, getPendingEmail, getUserLoadingState, setAuthFlow } from '../../../store/slices/authSlice';
import CustomButton, { BUTTON_STYLE_TYPE } from '../../common/Button';
import PasswordRequirementsChecklist from './PasswordRequirementsChecklist';
import styles from './styles.module.scss';

// At least 8 chars, 1 digit, 1 uppercase letter, 1 non-alphanumeric ("unique") character.
const PASSWORD_REGEX = /^(?=.*\d)(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}$/;

const SetPasswordStep = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isDesktop = useIsDesktopViewport();
    const email = useSelector(getPendingEmail);
    const loading = useSelector(getUserLoadingState);
    const error = useSelector(getAuthError);
    // HORO-10509: independent show/hide toggles — revealing the password
    // field shouldn't also reveal confirm-password, and vice versa.
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const {
        register,
        handleSubmit,
        watch,
        trigger,
        formState: { errors, isValid, touchedFields }
    } = useForm({ mode: 'onChange' });
    const passwordValue = watch('password', '');

    // react-hook-form's `isValid` defaults to true until the first
    // validation pass — without this, the Continue button briefly renders
    // enabled on mount even though both fields are empty.
    useEffect(() => {
        trigger();
    }, [trigger]);

    const onSubmit = ({ password, confirmPassword }) => {
        if (loading) {
            return;
        }
        dispatch(emailSetPassword({ email, password, confirmPassword }));
    };

    // HORO-10509: confirmed via Figma inspector — the desktop card
    // ("Registeration_general") uses different title copy ("Set password"
    // vs mobile's "Enter password"), an extra subtitle mobile doesn't
    // have, "Create account" instead of "Continue" on the submit button,
    // and Roboto 400/16px field labels instead of mobile's bold/14px
    // `.fieldLabel` — real per-breakpoint differences, not assumptions.
    const fieldLabelClassName = isDesktop ? `${styles.fieldLabel} ${styles.fieldLabelDesktop}` : styles.fieldLabel;

    return (
        <>
            <div className={styles.signInHeader}>
                <button
                    type="button"
                    className={styles.backIconBtn}
                    aria-label={t('buttons.back')}
                    onClick={() => dispatch(setAuthFlow('entry'))}
                >
                    <ArrowLeft size={24} color="#FFFFFF" />
                </button>
                <h2 className={`${styles.title} ${styles.titleMixedCase}`}>
                    {isDesktop ? t('modal.setPasswordTitleDesktop') : t('modal.setPasswordTitle')}
                </h2>
                {/* Balances the back button's width so the title centers on
                    the full row, not just the space left of the button —
                    same treatment as Sign in's header (confirmed centered
                    via Figma's inspector on both the mobile and desktop
                    "Enter/Set password" frames — desktop's 32×32 ArrowLeft
                    box matches .backIconBtn's own size exactly, so this
                    whole header row is shared as-is, no desktop overrides
                    needed). */}
                <div className={styles.headerSpacer} aria-hidden="true" />
            </div>
            {isDesktop && <p className={styles.subtitle}>{t('modal.setPasswordSubtitleDesktop')}</p>}
            <form onSubmit={handleSubmit(onSubmit)}>
                <span className={fieldLabelClassName}>{t('modal.passwordFieldLabel')}</span>
                <div className={styles.inputWrapper}>
                    <div className={styles.passwordFieldWrap}>
                        <input
                            className={`${styles.fieldInput} ${styles.fieldInputWithToggle}`}
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder={t('modal.passwordPlaceholder')}
                            {...register('password', { required: true, pattern: PASSWORD_REGEX })}
                        />
                        <button
                            type="button"
                            className={styles.passwordToggleBtn}
                            aria-label={showPassword ? t('buttons.hidePassword') : t('buttons.showPassword')}
                            onClick={() => setShowPassword((prev) => !prev)}
                        >
                            {showPassword ? <EyeClosed size={20} color="#FFFFFF" /> : <Eye size={20} color="#FFFFFF" />}
                        </button>
                    </div>
                </div>
                <span className={fieldLabelClassName}>{t('modal.passwordFieldLabelConfirm')}</span>
                <div className={styles.inputWrapper}>
                    <div className={styles.passwordFieldWrap}>
                        <input
                            className={`${styles.fieldInput} ${styles.fieldInputWithToggle}`}
                            type={showConfirmPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder={t('modal.confirmPasswordPlaceholder')}
                            {...register('confirmPassword', { required: true, validate: (value) => value === watch('password') })}
                        />
                        <button
                            type="button"
                            className={styles.passwordToggleBtn}
                            aria-label={showConfirmPassword ? t('buttons.hidePassword') : t('buttons.showPassword')}
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                        >
                            {showConfirmPassword ? <EyeClosed size={20} color="#FFFFFF" /> : <Eye size={20} color="#FFFFFF" />}
                        </button>
                    </div>
                    {errors.confirmPassword && touchedFields.confirmPassword && (
                        <span className={styles.error}>{t('errors.passwordsMustMatch', 'Passwords must match')}</span>
                    )}
                </div>
                <PasswordRequirementsChecklist password={passwordValue} />
                {error && <p className={styles.formError}>{error.message}</p>}
                <div className={styles.submitBtnWrap}>
                    <CustomButton
                        text={isDesktop ? t('buttons.createAccount') : t('buttons.continue')}
                        loading={loading}
                        disable={loading || !isValid}
                        styleType={BUTTON_STYLE_TYPE.PURPLE}
                    />
                </div>
            </form>
            {/* HORO-10509: the desktop Figma frame doesn't show this footer
                link at all (unlike mobile) — omitted here to match, rather
                than assuming it should carry over. */}
            {!isDesktop && (
                <p className={styles.helperText}>
                    {t('modal.alreadyHaveAccount')}{' '}
                    <button type="button" className={styles.helperTextAction} onClick={() => dispatch(setAuthFlow('sign-in'))}>
                        {t('modal.signInLink')}
                    </button>
                </p>
            )}
        </>
    );
};

export default SetPasswordStep;
